import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale
} from 'chart.js';
import { Bar,  Pie, Radar } from 'react-chartjs-2';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { motion } from 'framer-motion';
import '../styles/UserProgressAnalysisPage.css';

// Chart.js kayıtları
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale
);

const UserProgressAnalysisPage = () => {
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    // Sayfa yüklendiğinde animasyonları başlat
    setTimeout(() => {
      setLoading(false);

    }, 1000);
  }, []);

  // Renk paleti - daha soft renkler
  const colorPalette = {
    preTest: '#ff9999',
    postTest: '#7dd3c0',
    improvement: '#4fd1c7',
    decline: '#ffb366',
    primary: '#8fa7ff',
    secondary: '#9575cd',
    background: '#fafbfc'
  };

  // Genel gelişim istatistikleri - aktif öğrenci sayısı kaldırıldı
  const OverviewStats = () => (
    <div className="progress-overview-cards">
      <motion.div
        className="progress-stat-card improvement"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="card-icon">
          <i className="fas fa-chart-line"></i>
        </div>
        <div className="card-content">
          <h3>Genel Gelişim Oranı</h3>
          <div className="progress-container">
            <CircularProgressbar
              value={78}
              maxValue={100}
              text="78%"
              styles={buildStyles({
                pathColor: colorPalette.improvement,
                textColor: colorPalette.improvement,
                trailColor: '#e2e8f0',
              })}
            />
          </div>
          <p>Öğrencilerin %78'i gelişim gösterdi</p>
        </div>
      </motion.div>

      <motion.div
        className="progress-stat-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="card-icon">
          <i className="fas fa-trophy"></i>
        </div>
        <div className="card-content">
          <h3>Ortalama Puan Artışı</h3>
          <div className="score-improvement">
            <span className="score-change">+24.5</span>
            <span className="score-unit">puan</span>
          </div>
          <p>Öntest: 62.3 → Sontest: 86.8</p>
        </div>
      </motion.div>

      <motion.div
        className="progress-stat-card success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="card-icon">
          <i className="fas fa-medal"></i>
        </div>
        <div className="card-content">
          <h3>En Yüksek Gelişim</h3>
          <div className="max-improvement">
            <span className="improvement-score">+45</span>
            <span className="improvement-unit">puan</span>
          </div>
          <p>Bireysel en yüksek gelişim</p>
        </div>
      </motion.div>
    </div>
  );

  // Temel puan karşılaştırma grafiği
  const ScoreComparisonChart = () => {
    const chartData = {
      labels: ['Öntest', 'Sontest'],
      datasets: [{
        label: 'Ortalama Puan',
        data: [62.3, 86.8],
        backgroundColor: [colorPalette.preTest, colorPalette.postTest],
        borderColor: [colorPalette.preTest, colorPalette.postTest],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      }]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Öntest vs Sontest Ortalama Puanları',
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => `${context.label}: ${context.parsed.y} puan`
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          grid: {
            color: 'rgba(0,0,0,0.1)'
          }
        }
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuart'
      }
    };

    return (
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Bar data={chartData} options={options} />
      </motion.div>
    );
  };

  // Gelişim yüzdeleri analizi
  const ImprovementPercentageChart = () => {
    const chartData = {
      labels: ['Büyük Gelişim (>30%)', 'Orta Gelişim (15-30%)', 'Az Gelişim (5-15%)', 'Değişim Yok (±5%)', 'Gerileme (<-5%)'],
      datasets: [{
        data: [35, 43, 15, 5, 2],
        backgroundColor: [
          '#06d6a0',
          '#4ecdc4',
          '#ffd93d',
          '#e2e8f0',
          '#ff6b6b'
        ],
        borderWidth: 0,
        hoverBorderWidth: 3,
        hoverBorderColor: '#fff'
      }]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            usePointStyle: true,
            font: {
              size: 12
            }
          }
        },
        title: {
          display: true,
          text: 'Öğrenci Gelişim Kategorileri Dağılımı (%)',
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => `${context.label}: %${context.parsed} öğrenci`
          }
        }
      },
      animation: {
        animateRotate: true,
        animateScale: true,
        duration: 2000
      }
    };

    return (
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Pie data={chartData} options={options} />
      </motion.div>
    );
  };

  // Başarı kategorileri dağılımı
  const SuccessCategoriesChart = () => {
    const chartData = {
      labels: ['90-100', '80-89', '70-79', '60-69', '50-59', '0-49'],
      datasets: [
        {
          label: 'Öntest',
          data: [5, 12, 25, 35, 20, 3],
          backgroundColor: 'rgba(255, 107, 107, 0.7)',
          borderColor: colorPalette.preTest,
          borderWidth: 2,
          borderRadius: 4,
        },
        {
          label: 'Sontest',
          data: [18, 32, 28, 15, 5, 2],
          backgroundColor: 'rgba(78, 205, 196, 0.7)',
          borderColor: colorPalette.postTest,
          borderWidth: 2,
          borderRadius: 4,
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Puan Aralıklarına Göre Öğrenci Dağılımı (%)',
          font: {
            size: 16,
            weight: 'bold'
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0,0,0,0.1)'
          },
          title: {
            display: true,
            text: 'Öğrenci Yüzdesi (%)'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Puan Aralığı'
          }
        }
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuart'
      }
    };

    return (
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Bar data={chartData} options={options} />
      </motion.div>
    );
  };

  // Zorluk seviyesi karşılaştırması
  const DifficultyComparisonChart = () => {
    const chartData = {
      labels: ['Kolay Sorular', 'Orta Sorular', 'Zor Sorular'],
      datasets: [
        {
          label: 'Öntest Başarı Oranı',
          data: [85, 62, 35],
          backgroundColor: 'rgba(255, 107, 107, 0.2)',
          borderColor: colorPalette.preTest,
          pointBackgroundColor: colorPalette.preTest,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6
        },
        {
          label: 'Sontest Başarı Oranı',
          data: [92, 88, 72],
          backgroundColor: 'rgba(78, 205, 196, 0.2)',
          borderColor: colorPalette.postTest,
          pointBackgroundColor: colorPalette.postTest,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Zorluk Seviyesine Göre Başarı Oranları (%)',
          font: {
            size: 16,
            weight: 'bold'
          }
        }
      },
      scales: {
        r: {
          angleLines: {
            display: true
          },
          suggestedMin: 0,
          suggestedMax: 100,
          ticks: {
            stepSize: 20
          }
        }
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuart'
      }
    };

    return (
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8 }}
      >
        <Radar data={chartData} options={options} />
      </motion.div>
    );
  };

  // Hazır istatistik görselleri - modal özelliği eklendi
  const StatisticsImages = () => (
    <div className="statistics-images-grid">
      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        onClick={() => setSelectedImage('/stats/temel_puan_karsilastirmasi.png')}
      >
        <img src="/stats/temel_puan_karsilastirmasi.png" alt="Temel Puan Karşılaştırması" />
        <div className="image-info">
          <h4>Temel Puan Karşılaştırması</h4>
          <p>Öntest ve sontest arasındaki temel puan farkları ve genel gelişim trendi</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        onClick={() => setSelectedImage('/stats/gelisim_yuzdeleri_analizi.png')}
      >
        <img src="/stats/gelisim_yuzdeleri_analizi.png" alt="Gelişim Yüzdeleri Analizi" />
        <div className="image-info">
          <h4>Gelişim Yüzdeleri Analizi</h4>
          <p>Öğrencilerin bireysel gelişim yüzdelerinin detaylı analizi ve dağılımı</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        onClick={() => setSelectedImage('/stats/basari_dagilimi_analizi.png')}
      >
        <img src="/stats/basari_dagilimi_analizi.png" alt="Başarı Dağılımı Analizi" />
        <div className="image-info">
          <h4>Başarı Dağılımı Analizi</h4>
          <p>Puan aralıklarına göre öğrenci dağılımı ve başarı seviyelerinin analizi</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        onClick={() => setSelectedImage('/stats/basari_kategorileri_dagilimi.png')}
      >
        <img src="/stats/basari_kategorileri_dagilimi.png" alt="Başarı Kategorileri Dağılımı" />
        <div className="image-info">
          <h4>Başarı Kategorileri Dağılımı</h4>
          <p>Öğrencilerin başarı kategorilerine göre dağılımı ve performans seviyeleri</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        onClick={() => setSelectedImage('/stats/zorluk_karsilastirmasi.png')}
      >
        <img src="/stats/zorluk_karsilastirmasi.png" alt="Zorluk Karşılaştırması" />
        <div className="image-info">
          <h4>Zorluk Seviyesi Karşılaştırması</h4>
          <p>Farklı zorluk seviyelerindeki soruların öntest ve sontest karşılaştırması</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4 }}
        onClick={() => setSelectedImage('/stats/soru_analizi_PreTest.png')}
      >
        <img src="/stats/soru_analizi_PreTest.png" alt="Öntest Soru Analizi" />
        <div className="image-info">
          <h4>Öntest Soru Analizi</h4>
          <p>Öntest sorularının bireysel analizi ve öğrenci performansları</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        onClick={() => setSelectedImage('/stats/soru_analizi_PostTest.png')}
      >
        <img src="/stats/soru_analizi_PostTest.png" alt="Sontest Soru Analizi" />
        <div className="image-info">
          <h4>Sontest Soru Analizi</h4>
          <p>Sontest sorularının bireysel analizi ve gelişim göstergeleri</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        onClick={() => setSelectedImage('/stats/en_dusuk_puan_sorular_analizi_PreTest.png')}
      >
        <img src="/stats/en_dusuk_puan_sorular_analizi_PreTest.png" alt="Öntest Zayıf Alan Analizi" />
        <div className="image-info">
          <h4>Öntest Zayıf Alan Analizi</h4>
          <p>Öntestte en düşük puan alınan sorular ve gelişim alanları</p>
        </div>
      </motion.div>

      <motion.div
        className="image-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        onClick={() => setSelectedImage('/stats/en_dusuk_puan_sorular_analizi_PostTest.png')}
      >
        <img src="/stats/en_dusuk_puan_sorular_analizi_PostTest.png" alt="Sontest Zayıf Alan Analizi" />
        <div className="image-info">
          <h4>Sontest Zayıf Alan Analizi</h4>
          <p>Sontestte hala gelişim gerektiren alanlar ve öneriler</p>
        </div>
      </motion.div>
    </div>
  );

  // Sayfa görünüm kontrolleri - detaylı analiz kaldırıldı
  const ViewControls = () => (
    <div className="view-controls">
      <button
        className={selectedView === 'overview' ? 'active' : ''}
        onClick={() => setSelectedView('overview')}
      >
        <i className="fas fa-chart-pie"></i>
        Genel Bakış
      </button>
      <button
        className={selectedView === 'comparison' ? 'active' : ''}
        onClick={() => setSelectedView('comparison')}
      >
        <i className="fas fa-balance-scale"></i>
        Karşılaştırma
      </button>
      <button
        className={selectedView === 'reports' ? 'active' : ''}
        onClick={() => setSelectedView('reports')}
      >
        <i className="fas fa-file-chart"></i>
        Raporlar
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Gelişim analizleri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="user-progress-analysis-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <i className="fas fa-chart-line"></i>
            Kullanıcı Gelişim Analizleri
          </h1>
          <p>Kurs öncesi ve sonrası sınav sonuçlarına dayalı detaylı gelişim raporları</p>
        </div>
      </div>

      <ViewControls />

      <div className="analysis-content">
        {selectedView === 'overview' && (
          <>
            <OverviewStats />
            <div className="charts-grid">
              <ScoreComparisonChart />
              <ImprovementPercentageChart />
            </div>
          </>
        )}

        {selectedView === 'comparison' && (
          <div className="charts-grid">
            <ScoreComparisonChart />
            <SuccessCategoriesChart />
            <DifficultyComparisonChart />
          </div>
        )}

        {selectedView === 'reports' && (
          <StatisticsImages />
        )}
      </div>

      {/* Modal için görsel */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="Büyütülmüş Görsel" />
            <button className="close-modal" onClick={() => setSelectedImage(null)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProgressAnalysisPage;
